import { ClaimRow } from 'api/endpoints';
import Excel from 'exceljs';
import { formatDate, US_DATE_FORMAT } from '../../../utils/formatDate';
import { apiExhibitExport } from '../api/apiExhibitExporter';
import { StructureFooter } from '../typesExhibitExporter';
import { getClaimNumber } from '../utils/field';
import { IExcelCreator } from './excelExporter';
import { ExhibitTemplateProps } from './exhibitType';
import {
  createTotalRowStyle,
  defCellStyle,
  defPositionNumberStyle,
} from './utils/excelConst';
import {
  createExcelWorkbook,
  ExcelDocumentHeader,
} from './utils/excelCreateWorkbook';
import { excelAddPageHeader, HeaderPositionCell } from './utils/excelHeader';
import {
  deleteObjectionCommentaryColumn,
  excelAddPageFooter,
  excelAddRow,
  excelAddTableHeaderRow,
  excelAddTotalRow,
} from './utils/excelUtils';
import {
  createClaimAmount,
  createHeaderFooter,
  getAddressLine,
  getChildDataByRefNumber,
  getTotalCurrency,
} from './utils/exporterUtils';
import { IExhibitWorkbookData, IRowValues, TRowStyle } from './utils/type';

const COLUMN_LIST: ExcelDocumentHeader[] = [
  { header: '', width: 4 },
  { header: 'Original creditor', width: 20 },
  { header: 'Claim transferred to', width: 10.1 },
  { header: 'Claim number', width: 10.1 },
  { header: 'Original Debtor', width: 17.8 },
  { header: 'Proposed Debtor', width: 17.8 },
  { header: 'Date filed/scheduled', width: 10 },
  { header: 'Claim amount and priority (1)', width: 13 },
  { header: 'Basis for objection', width: 15 },
  { header: 'Comments', width: 15 },
];

const valuesStyle: TRowStyle = [
  { ...defPositionNumberStyle }, //'positionNumber',
  { ...defCellStyle }, //'originalCreditor',
  { ...defCellStyle }, //'claimTransferredTo',
  { ...defCellStyle }, //'referenceNumber',
  { ...defCellStyle }, //'originalDebtor',
  { ...defCellStyle }, //'proposedDebtor',
  {
    ...defCellStyle,
    numFmt: 'mm-dd-yy',
    alignment: { horizontal: 'center', vertical: 'top' },
  }, //'officialClaimDate',
  {
    ...defCellStyle,
    numFmt: '[$$-409]#,##0.00',
    alignment: { horizontal: 'right', vertical: 'top', wrapText: true },
  }, //'claimAmount',
  { ...defCellStyle }, //'objectionBasis',
  { ...defCellStyle }, //'objectionCommentary',
];

const valuesTotalStyle = createTotalRowStyle(valuesStyle.length);

const objectionColId = 10;

const PAGE_HEADER_POS: HeaderPositionCell = {
  left: 1,
  middle: 4,
  right: 7,
  length: 10,
};

export const templateAlterDebtor: IExcelCreator = {
  fileName: 'Alter_Debtor.xlsx',
  getWorkbook: async (props: ExhibitTemplateProps) => {
    const { displayObjection } = props.exhibit.structure;
    const excelData = await getDataToShow(props);
    return await createWorkbook(excelData, displayObjection);
  },
};

async function createWorkbook(
  data: IExhibitWorkbookData,
  displayObjection: boolean | undefined,
): Promise<Excel.Workbook> {
  const workbook = createExcelWorkbook('Sheet1', COLUMN_LIST);
  const firstSheet = workbook.getWorksheet(1);

  excelAddPageHeader(firstSheet, data.headerFooter.header, PAGE_HEADER_POS);
  excelAddTableHeaderRow(firstSheet, COLUMN_LIST);
  excelAddPageFooter(firstSheet, data.headerFooter);

  data.claimsList.forEach((row) => excelAddRow(firstSheet, row, valuesStyle));
  excelAddTotalRow(firstSheet, data.totalRow, valuesTotalStyle);

  deleteObjectionCommentaryColumn(firstSheet, displayObjection, objectionColId);
  return workbook;
}

export async function getDataToShow(
  props: ExhibitTemplateProps,
): Promise<IExhibitWorkbookData> {
  const { exhibit, claimItems } = props;
  const { footer } = exhibit.structure;
  const { resolutionActionNameId } = exhibit;

  const showClaimCount = footer.includes(StructureFooter.ClaimCount);

  const childClaims = await apiExhibitExport.getChildClaims(resolutionActionNameId);
  // we need to remove parent claims when we calculate totals
  // we get the totals from its child claims
  const filteredClaims = claimItems.filter(
    (claim) =>
      !childClaims.find((c) => c.parentClaimNumber === claim.referenceNumber),
  );

  return {
    headerFooter: createHeaderFooter(props),
    claimsList: getPreparedClaimsData(
      claimItems,
      exhibit.structure.displayObjection,
      exhibit.objectionBasis,
      childClaims,
    ),
    totalRow: getTotalRow([...filteredClaims, ...childClaims], showClaimCount),
  };
}

function getTotalRow(claims: ClaimRow[], showClaimCount: boolean): IRowValues {
  const values = [
    '', //positionNumber
    'Claims to be modified totals', //originalCreditor
    '', //claimTransferredTo
    showClaimCount ? `Count: ${claims.length}` : '', //referenceNumber
    '', //originalDebtor
    '', //proposedDebtor
    '', //officialClaimDate
    getTotalCurrency(claims, 'currentTotal'), //claimAmount
    '', //objectionBasis
  ];
  return { values };
}

const getPreparedClaimsData = (
  claims: ClaimRow[],
  displayObjection: boolean | undefined,
  objectionBasis: string,
  childClaims: ClaimRow[],
) => {
  return claims.map((claim, index) => {
    const child = getChildDataByRefNumber(childClaims, claim.referenceNumber);
    return {
      values: [
        index + 1, //positionNumber
        getAddressLine(claim, claim.redactNameAddress), //originalCreditor
        '', //claimTransferredTo
        getClaimNumber(claim), //referenceNumber
        child.originalDebtors ?? claim.originalDebtor, //originalDebtor
        child.proposedDebtors ?? claim.proposedDebtor, //proposedDebtor
        formatDate(claim.officialClaimDate, US_DATE_FORMAT), //officialClaimDate
        child.claimAmounts ?? createClaimAmount(claim), //claimAmount
        objectionBasis, //objectionBasis
        displayObjection ? claim.objectionCommentary : '', //objectionCommentary
      ],
    } as IRowValues;
  });
};
